import { getLocationTypeLabel } from './locationTypes';

/**
 * Titel und Beschreibungen für die Verzeichnisseiten.
 *
 * Der vom Imker vergebene Name steht nicht mehr allein im Titel. Die interne
 * Auswertung über 191 Tage zeigt: Einträge, deren Name das Wort Honig enthält,
 * werden bis zu viermal häufiger geöffnet als solche mit einer Adresse oder einem
 * branchenfremden Firmennamen — 7,8 Prozent gegen 1,9 Prozent. Der Name bleibt
 * sichtbar, aber als Zusatz hinter der Angabe, wonach jemand tatsächlich sucht.
 */

/**
 * Bundeslaender kommen aus der API teils englisch: "Hesse" neben "Sachsen".
 *
 * Nach "Imker in Hesse" sucht niemand. Der Name wird deshalb fuer die Anzeige
 * uebersetzt; die Adresse bleibt, wie sie ist - sie stammt aus den Daten und
 * soll sich nicht aendern, nur weil ein Name anders geschrieben wird.
 */
const BUNDESLAND_AUF_DEUTSCH = {
    'hesse': 'Hessen',
    'bavaria': 'Bayern',
    'lower saxony': 'Niedersachsen',
    'north rhine-westphalia': 'Nordrhein-Westfalen',
    'saxony': 'Sachsen',
    'saxony-anhalt': 'Sachsen-Anhalt',
    'thuringia': 'Thüringen',
    'rhineland-palatinate': 'Rheinland-Pfalz',
    'mecklenburg-western pomerania': 'Mecklenburg-Vorpommern',
    'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
    'baden-wurttemberg': 'Baden-Württemberg',
    'baden-württemberg': 'Baden-Württemberg'
};

export const bundeslandName = (wert) => {
    const text = (wert || '').toString().trim();
    if (!text) return '';
    return BUNDESLAND_AUF_DEUTSCH[text.toLowerCase()] || grossschreiben(text);
};

const grossschreiben = (wert) => {
    const text = (wert || '').toString().trim();
    if (!text) return '';
    return text
        .split(/[\s-]+/)
        .map((teil) => teil.charAt(0).toUpperCase() + teil.slice(1))
        .join(text.includes('-') ? '-' : ' ');
};

const zahlwort = (n, einzahl, mehrzahl) => `${n} ${n === 1 ? einzahl : mehrzahl}`;

export const detailTitel = (loc = {}) => {
    const ort = grossschreiben(loc.city);
    const name = (loc.title || '').trim();
    const basis = ort ? `Honig direkt vom Imker in ${ort}` : 'Honig direkt vom Imker';
    return name ? `${basis} — ${name}` : basis;
};

export const detailBeschreibung = (loc = {}) => {
    const ort = grossschreiben(loc.city);
    const art = getLocationTypeLabel(loc.locationType);
    const sortiment = (loc.categories || []).map((c) => c.title).filter(Boolean).slice(0, 3);

    const teile = [];
    teile.push(art ? `${art} für Honig` : 'Verkaufsstelle für Honig');
    if (ort) teile.push(`in ${ort}`);
    if (sortiment.length) teile.push(`— ${sortiment.join(', ')}`);
    teile.push('direkt vom Imker.');

    return teile.join(' ').replace(/\s+—\s+/, ' — ');
};

export const ortsTitel = (ort, anzahl) => {
    const name = grossschreiben(ort);
    if (!anzahl) return `Honig kaufen in ${name}`;
    return `Honig kaufen in ${name} — ${zahlwort(anzahl, 'Verkaufsstelle', 'Verkaufsstellen')}`;
};

export const ortsBeschreibung = (ort, anzahl, automaten = 0) => {
    const name = grossschreiben(ort);
    const kern = `${zahlwort(anzahl, 'Verkaufsstelle', 'Verkaufsstellen')} für Honig direkt vom Imker in ${name}`;
    return automaten > 0
        ? `${kern}, darunter ${zahlwort(automaten, 'Automat', 'Automaten')} rund um die Uhr.`
        : `${kern}. Hofläden, Automaten und Selbstbedienungsstände in deiner Nähe.`;
};

export const bundeslandTitel = (bundesland, anzahl) => {
    const name = bundeslandName(bundesland);
    if (!anzahl) return `Honig direkt vom Imker in ${name}`;
    return `Honig direkt vom Imker in ${name} — ${zahlwort(anzahl, 'Verkaufsstelle', 'Verkaufsstellen')}`;
};

export const automatenTitel = (ort) =>
    `Honigautomat in ${grossschreiben(ort)} — rund um die Uhr geöffnet`;

export const hofladenTitel = (ort, anzahl) => {
    const name = grossschreiben(ort);
    if (!anzahl) return `Hofladen mit Honig in ${name}`;
    return `Hofladen mit Honig in ${name} — ${zahlwort(anzahl, 'Adresse', 'Adressen')}`;
};

/**
 * Ein Satz in Klartext für die Ortsseite.
 *
 * Sprachmodelle zitieren, was sich ohne Auswertung von Markup herauslesen lässt.
 * Deshalb steht die Zahl als Fließtext auf der Seite und nicht nur in einer Liste.
 */
export const ortsAntwortsatz = (ort, anzahl, automaten = 0) => {
    const name = grossschreiben(ort);
    if (!anzahl) {
        return `In ${name} ist derzeit keine Verkaufsstelle für Honig direkt vom Imker eingetragen.`;
    }
    const kern = `In ${name} gibt es ${zahlwort(anzahl, 'Verkaufsstelle', 'Verkaufsstellen')} für Honig direkt vom Imker`;
    return automaten > 0
        ? `${kern}, darunter ${zahlwort(automaten, 'Automat', 'Automaten')}, die rund um die Uhr zugänglich sind.`
        : `${kern}.`;
};

export const zaehleAutomaten = (locations = []) =>
    locations.filter((l) => Number(l.locationType) === 3).length;

export { grossschreiben };
